import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, startCase } from 'lodash'

import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useHistory, Link, useParams } from "react-router-dom";
import LinearProgress from '@material-ui/core/LinearProgress';

import { ReactComponent as LeftArrowIcon } from '../../assets/icons/arrow-left.svg'
import { ReactComponent as OpenSidebarIcon } from '../../assets/icons/open_sidebar.svg'
import IconClose from '../icons/IconClose'
import NavbarSwitches from '../Layout/NavbarSwitches'
import PrinterSetInfo from './PrinterSetInfo'
import SearchBar from '../SearchBar'
import { DialogModal } from '../DialogModal'
import Dropdown from '../../components/Dropdown'

import { searchSuitesStaff, setAttendantStatusFilterValue } from '../../store/Staff/actions'
import { searchCustomer } from '../../store/Customer/actions'

import { createOrderCapture, fetchOrderCaptureStatus } from '../../utils/api'

const Navbar = ({
  event,
  backTo,
  iconType,
  subtitle,
  title,
  toggleSidebarActive,
  transparent = false,
  twoToned = false,
}) => {
  const [showSettlementDialogModal, setShowSettlementDialogModal] = useState(false)
  const [transactionSettledStatus, setTransactionSettledStatus] = useState('confirm')
  const [batchSettlementResponse, setBatchSettlementResponse] = useState({})
  const dispatch = useDispatch()
  const history = useHistory()
  const { eventId } = useParams()
  const attendantStatusFilterValue = useSelector(state => state?.staff?.attendantStatusFilterValue ?? 'all')
  let iconClass = classNames('navbar__back-icon', { transparent, 'two-toned': twoToned })

  let action = <button
                aria-label="Sidebar toggle"
                className="navbar__menu"
                onClick={toggleSidebarActive}
                type="button">
                  <OpenSidebarIcon className="navbar__menu-icon" />
                </button>

  if (iconType && backTo) {
    if (iconType === 'back') {
      action = (
      <Link to={backTo} className='back-arrow-wrapper'>
        <LeftArrowIcon className={iconClass} />
      </Link>
      )
    } else if (iconType === 'close') {
      iconClass = classNames('navbar__close-icon', { transparent, 'two-toned': twoToned })
      action = <IconClose backTo={backTo} className={iconClass}/>
    }
  }

  const renderActiveInactiveAttendantsDropdown = () => {
    const options = [
      {value: 'all', label: 'All Attendants'},
      {value: 'active', label: 'Active Attendants'},
      {value: 'inactive', label: 'Inactive Attendants'}
    ]
    const displayValue = options.find((status) => status.value === attendantStatusFilterValue)

    const handleChange = (event) => {
      const selectedValue = event.target.value

      dispatch(setAttendantStatusFilterValue(selectedValue))
    }

    return (
      <div className='staff-status'>
        <p className='title'>{displayValue?.label}</p>
        <Dropdown
          className="attendants-status"
          dark
          fullWidth
          isValid
          label="Attendants status"
          onChange={handleChange}
          options={options}
          required
          value={displayValue?.value}
        />
      </div>
    )
  }

  const renderNavbarActions = () => {
    let handleSearch

    if (title === 'Staff') {
      handleSearch = event => dispatch(searchSuitesStaff(event))
    } else if (title === 'Customers') {
      handleSearch = event => dispatch(searchCustomer(event))
    }

    return (
      <>
        <SearchBar handleSearch={handleSearch} />
        {title === 'Staff' && (
          <button
            className='btn new-attendant'
            onClick={() => history.push('/staff/new')}
          >
            +
          </button>
        )}
      </>
    )
  }

  let navbarClass = classNames('navbar', {
    'event-navbar': eventId,
    'staff-navbar': title === 'Staff'
  })
  let titleClass = 'navbar__left__title'

  if (iconType === 'close') {
    navbarClass = classNames('navbar', 'navbar-close')
    titleClass = classNames('navbar__left__title', 'title-close')
  }

  useEffect(() => {
    let intervalId
    if(transactionSettledStatus === 'loading' && !intervalId) {
      intervalId = setInterval(() => {
        fetchOrderCaptureStatus(event.uuid).then((resp) => {
          setBatchSettlementResponse(resp.data)
          if(resp.data.progress === 'completed') {
            clearInterval(intervalId)
            setTransactionSettledStatus('settled')
          }
        }).catch(() => {
          clearInterval(intervalId)
          setTransactionSettledStatus('error')
        })
      }, 5000)
    }
    return () => {
      clearInterval(intervalId)
    }
  }, [transactionSettledStatus, event.uuid])

  const renderSettlementDialogModal = () => {
    let modalButtonText
    if (transactionSettledStatus === 'settled') {
      modalButtonText = 'Done'
    } else if(transactionSettledStatus === 'error') {
      modalButtonText = 'Close'
    } else {
      modalButtonText = 'Confirm'
    }

    const renderModalTitle = () => {
      if (isEmpty(batchSettlementResponse)) {
        return 'Settle Transactions'
      } else {
        return startCase(batchSettlementResponse.progress)
      }
    }

    const handleConfirmationCallback = () => {
      if (transactionSettledStatus === 'settled' || transactionSettledStatus === 'error') {
        setShowSettlementDialogModal(false)
        setTransactionSettledStatus('confirm')
        setBatchSettlementResponse({})
      }

      if (transactionSettledStatus === 'confirm' ) {
        createOrderCapture(event.uuid)
        .then(() => {
          setTransactionSettledStatus('loading')
        })
        .catch(() => {
          setTransactionSettledStatus('error')
        })

      }
    }

    const modalBody = () => {
      switch (transactionSettledStatus) {
        case 'error':
          return(
            <span className='settlement-modal-body'>
              Something went wrong
            </span>
          )
        case 'loading':
          return (
            <LinearProgress className='linear-progress' />
          )

        case 'settled':
          const { results, message } = batchSettlementResponse
          const resultsKeys = Object.keys(results)
          const renderOrderItems = () => resultsKeys.map((item, index) => (
              <span className="settlement-modal-body__order-item">
                {`Order ${item} - ${Object.values(results)[index]}`}
              </span>
            ))

          return (
            <span className='settlement-modal-body'>
              {message}
              {!isEmpty(results) && renderOrderItems()}
            </span>
          )

        default:
          return (
            <span className='settlement-modal-body'>
              By confirming, all closed orders will be settled and cannot be re-opened.
            </span>
          )
      }
    }

    return (
      <DialogModal
        autoHandleCloseModal={false}
        className='settlements-modal'
        closeModalCallback={setShowSettlementDialogModal}
        confirmationCallback={handleConfirmationCallback}
        isCancelButton={transactionSettledStatus === 'confirm'}
        modalBody={modalBody()}
        modalButtonText={transactionSettledStatus !== 'loading' && modalButtonText}
        modalIsOpen={showSettlementDialogModal}
        modalTitle={renderModalTitle()}
      />
    )
  }

  return (
    <>
      <div className='empty' />
      <div className={navbarClass}>
        <div className='navbar__left'>
            <div className={titleClass}>
              {action}{title}
            </div>
            {title === 'Staff' && renderActiveInactiveAttendantsDropdown()}
          {subtitle ? <div className="navbar__left__subtitle">{` - ${subtitle}`}</div> : null}
          <PrinterSetInfo event={event} />
        </div>
        <div className="navbar__right">
          {['Customers', 'Staff'].includes(title) && renderNavbarActions()}
          {eventId && (
            <button
              className='navbar__right__settlement'
              onClick={() => setShowSettlementDialogModal(true)}
            >
              Settle Transactions
            </button>
          )}
          {eventId && <NavbarSwitches />}
          {renderSettlementDialogModal()}
        </div>
      </div>
    </>
  )
}

Navbar.defaultProps = {
  backTo: '',
  event: {},
  iconType: '',
  title: '',
  toggleSidebarActive: () => {}
}

Navbar.propTypes = {
  backTo: PropTypes.oneOfType([ PropTypes.string, PropTypes.object ]),
  event: PropTypes.object,
  iconType: PropTypes.string,
  title: PropTypes.string,
  toggleSidebarActive: PropTypes.func,
}

export default Navbar
